import { useEffect, useState } from "react"
import { prefixLibraryLabel, type LibraryItem, type LibraryItemKind } from "@/components/library/library-item"
import { LibraryItems, LibraryItemsReporter } from "@/components/library/library-items"
import {
  LibraryNodeEmpty,
  LibraryNodeError,
  LibraryNodeLoading,
  type LibrarySlot,
} from "@/components/library/library-node-state"
import { useTranslation } from "@/hooks/use-translation"
import {
  fetchLibraryEntityFolder,
  type LibraryEntityFolder as FolderData,
  type LibraryEntityGroupRow,
  type LibraryEntityItemRow,
} from "@/lib/library-api"
import { previzSource, projectAssetSource } from "@/lib/library-source"
import type { LibraryEntitySection } from "@/lib/library-url"
import type { LibraryViewMode } from "@/lib/library-view-mode"

type Props = {
  projectId: number
  section: LibraryEntitySection
  entityId: number
  mode: LibraryViewMode
  focusedKey?: string | null
  onFocusItem?: (key: string) => void
  onOpenItem?: (item: LibraryItem) => void
  onItemsChange?: (items: LibraryItem[]) => void
}

type Translate = (key: string, options?: Record<string, unknown>) => string

const NO_ITEMS: LibraryItem[] = []

/**
 * Папка героя, локации, реквизита или 3D-сцены одним списком. Состав решает сервер,
 * здесь перевод подписей: имя бывшей группы становится префиксом, как «Шот 1.3 - Видео».
 */
export function LibraryEntityFolder({
  projectId,
  section,
  entityId,
  mode,
  focusedKey,
  onFocusItem,
  onOpenItem,
  onItemsChange,
}: Props) {
  const { t } = useTranslation(["workspace", "common"])
  const [slot, setSlot] = useState<LibrarySlot<FolderData>>({ status: "loading" })
  const [reloadKey, setReloadKey] = useState(0)
  // i18next-t не принимает произвольный ключ: прямой проброс в сборщик роняет tsc на перегрузках.
  const translate: Translate = (key, options) => (t as unknown as Translate)(key, options)

  useEffect(() => {
    // Запрос именно отменяем: дерево меняет папку на каждое нажатие стрелки.
    const controller = new AbortController()
    const { signal } = controller
    setSlot({ status: "loading" })
    fetchLibraryEntityFolder(projectId, section, entityId, { signal })
      .then((entity) => setSlot({ status: "ready", data: entity }))
      .catch(() => {
        if (!signal.aborted) setSlot({ status: "error" })
      })
    return () => controller.abort()
  }, [projectId, section, entityId, reloadKey])

  if (slot.status === "loading" || slot.status === "error") {
    return (
      <>
        <LibraryItemsReporter items={NO_ITEMS} onItemsChange={onItemsChange} />
        {slot.status === "loading" ? (
          <LibraryNodeLoading />
        ) : (
          <LibraryNodeError onRetry={() => setReloadKey((k) => k + 1)} />
        )}
      </>
    )
  }

  const entity = slot.data
  const items = [...entity.own.map((row) => toItem(row, translate)), ...flattenGroups(entity.groups, [], translate)]

  return (
    <>
      <LibraryItemsReporter items={items} onItemsChange={onItemsChange} />
      {items.length === 0 ? <LibraryNodeEmpty>{t("library.entity.empty")}</LibraryNodeEmpty> : null}
      {items.length > 0 ? (
        <LibraryItems
          items={items}
          mode={mode}
          listLayout="table"
          aspectClass="aspect-square"
          focusedKey={focusedKey}
          onFocusItem={onFocusItem}
          onOpenItem={onOpenItem}
        />
      ) : null}
    </>
  )
}

/** Именованная группа — «Образ Лена». Без своего имени заголовок повторяет вид плитки, префикс не нужен. */
function namedGroupTitle(group: LibraryEntityGroupRow, t: Translate): string | null {
  const own = group.label?.trim() ?? ""
  if (!own) return null
  const kindLabel = t(`library.entity.group.${group.kind}`, { defaultValue: group.kind })
  if (own.localeCompare(kindLabel, undefined, { sensitivity: "accent" }) === 0) return null
  return `${kindLabel} ${own}`
}

function flattenGroups(groups: LibraryEntityGroupRow[], ancestors: string[], t: Translate): LibraryItem[] {
  return groups.flatMap((group) => {
    const title = namedGroupTitle(group, t)
    const next = title ? [...ancestors, title] : ancestors
    const prefix = next.length > 0 ? next.join(" - ") : null
    return [
      ...group.items.map((row) => withGroupPrefix(toItem(row, t), prefix)),
      ...flattenGroups(group.groups ?? [], next, t),
    ]
  })
}

function withGroupPrefix(item: LibraryItem, prefix: string | null): LibraryItem {
  if (!prefix) return item
  const label = prefixLibraryLabel(prefix, item.label)
  return {
    ...item,
    label,
    asset: item.asset ? { ...item.asset, title: prefixLibraryLabel(prefix, item.asset.title) } : undefined,
  }
}

/**
 * Подпись плитки: перевод по label_kind, а своё имя (ракурс, файл, полнота листа)
 * уходит в meta — так плитка читается одинаково у всех видов сущностей.
 */
function toItem(row: LibraryEntityItemRow, t: Translate): LibraryItem {
  const kind: LibraryItemKind = row.media === "video" ? "video" : row.media === "audio" ? "audio" : "image"
  // У загруженного референса название — это имя файла, а перевод «Референс» ничего не
  // добавляет: на узкой плитке две подписи режутся обе. У сгенерированных листов наоборот —
  // название переводом, а своё (ракурс, полнота листа, образ) коротким хвостом.
  const isUpload = row.label_kind === "referenceInput"
  const isWallFace = row.label_kind === "wallFace"
  const label = isUpload
    ? (row.label ?? t("library.entity.item.referenceInput"))
    : t(`library.entity.item.${row.label_kind}`, { defaultValue: row.label_kind })
  // Стена: в модалке только вид, ключ грани остаётся в подписи плитки.
  // Если своё имя совпадает с видом («Ракурс»), в заголовке его не повторяем.
  const customName = isWallFace ? "" : (row.label?.trim() ?? "")
  const nameMatchesKind =
    customName.length > 0 && customName.localeCompare(label, undefined, { sensitivity: "accent" }) === 0
  const assetTitle = !isUpload && customName && !nameMatchesKind ? `${label} · ${customName}` : label
  const isPreviz = row.source?.entity_type === "previz"
  return {
    key: row.key,
    kind,
    label,
    meta: isUpload ? null : row.label,
    createdAt: row.created_at,
    thumbUrl: kind === "image" ? (row.thumb_url ?? row.url) : row.thumb_url,
    source: row.source
      ? isPreviz
        ? previzSource(row.source.entity_id)
        : projectAssetSource(row.source.entity_id)
      : undefined,
    pickMedia: isPreviz ? "previz" : undefined,
    asset: row.url
      ? {
          title: assetTitle,
          kind,
          url: kind === "video" ? null : row.url,
          videoUrl: kind === "video" ? row.url : null,
          downloadUrl: row.url,
          createdAt: row.created_at,
          durationMs: row.duration_ms ?? null,
        }
      : undefined,
  }
}
